import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserPlus, Home, User, LogOut, LogIn, Building, Video, 
  Star, Navigation, Award, MapPin, ChevronRight, 
  ChevronDown, Search, Lock, AlertTriangle, Stethoscope,
  Calendar, Menu
} from 'lucide-react';

import { DoctorCard } from '@/modules/consumer/components/DoctorCard';
import { GlobalNotificationBell } from '@/core/infrastructure/components/GlobalNotificationBell';
import { PasskeyBanner } from '@/shared/components/PasskeyBanner';
import { DirectoryService } from '@/core/application/directory/DirectoryService';
import { SearchIndexItem, SearchFilterType } from '@/core/domain/types/search.types';
import { DynamicLoader } from '@/shared/components/DynamicLoader';
import { AuthService } from '@/core/application/security/AuthService';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { useProfileStore } from '@/core/application/state/useProfileStore';
import { CityPickerModal } from '@/modules/consumer/components/CityPickerModal';
import { Ciudad } from '@/core/application/directory/GeografiaService';

interface UserLocation {
  lat: number;
  lng: number;
}

function usePatientSearchData() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchIndexItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const isClinical = useProfileStore(state => state.isClinical);
  const isProfileComplete = useProfileStore(state => state.isProfileComplete);
  const [gpsDenied, setGpsDenied] = useState(false);

  const [activeFilter, setActiveFilter] = useState<SearchFilterType>('cercania');
  
  const [isCityPickerVisible, setIsCityPickerVisible] = useState(false);
  const [targetCity, setTargetCity] = useState<Ciudad | null>(null);

  const [isEngineReady, setIsEngineReady] = useState(false);
  const [selectedModalities, setSelectedModalities] = useState<string[]>([]);

  const toggleModality = (modality: string) => {
    if (modality === 'todas') {
      setSelectedModalities([]);
    } else {
      setSelectedModalities([modality]);
    }
  };

  const fetchProfessionals = async (
    searchQuery: string = '', 
    lat: number | null = null, 
    lng: number | null = null,
    filter: SearchFilterType = 'cercania',
    cityId: number | null = null,
    modalities: string[] = []
  ) => {
    try {
      setLoading(true);
      const data = await DirectoryService.getProfessionals(searchQuery, lat, lng, filter, cityId, modalities);
      setResults(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error al buscar profesionales');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocation = (currentQuery: string) => {
    if (!('geolocation' in navigator)) {
      setGpsDenied(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setUserLocation({ lat, lng });
        if (!currentQuery) {
          fetchProfessionals('', lat, lng);
        }
      },
      (err) => {
        console.log("[GPS] Error al obtener ubicación:", err);
        setGpsDenied(true);
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    const initSearch = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setIsLoggedIn(true);
          const store = useProfileStore.getState();
          if (!store.hasChecked) {
            await store.fetchProfile();
          }
          
          if (!useProfileStore.getState().isProfileComplete) {
            navigate('/app/identity', { replace: true });
            return;
          }
        } else {
          setIsLoggedIn(false);
        }
        
        setCheckingAuth(false);

        await DirectoryService.initializeEngine();
        setIsEngineReady(true);
        
        fetchProfessionals('', null, null);
        fetchLocation(query);

      } catch (err) {
        console.error("Error inicializando búsqueda:", err);
      }
    };

    initSearch();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isEngineReady) return;
    const delayDebounceFn = setTimeout(() => {
      // Fix #1: truncar el query a 100 chars antes de pasarlo al motor
      const safeQuery = query.slice(0, 100);
      fetchProfessionals(
        safeQuery,
        userLocation?.lat || null, 
        userLocation?.lng || null, 
        activeFilter, 
        targetCity?.ciudad_id || null,
        selectedModalities
      );
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [query, userLocation, activeFilter, targetCity, selectedModalities, isEngineReady]);

  const requestGps = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
          setGpsDenied(false);
        },
        () => {
          globalThis.alert("Permisos Bloqueados: El acceso a la ubicación está bloqueado. Por favor, habilítalo en la barra de direcciones de tu navegador y recarga la página.");
        }
      );
    } else {
      globalThis.alert("Tu navegador no soporta geolocalización.");
    }
  };

  return {
    query, setQuery, results, loading, error, userLocation, isClinical, isLoggedIn, checkingAuth,
    isProfileComplete, gpsDenied, activeFilter, setActiveFilter, isCityPickerVisible, setIsCityPickerVisible,
    targetCity, setTargetCity, isEngineReady, selectedModalities, toggleModality, requestGps
  };
}

const ActionPills = ({ isClinical, isLoggedIn, checkingAuth, isProfileComplete }: any) => {
  const navigate = useNavigate();
  const profile = useProfileStore(state => state.profile);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleSecureNavigation = (route: string) => {
    setIsMenuOpen(false);
    if (isProfileComplete) navigate(route);
    else navigate('/app/identity');
  };

  if (checkingAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <div style={{ width: '20px', height: '20px', border: '2px solid #94A3B8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginBottom: '20px', position: 'relative', flexWrap: 'wrap' }}>
      {isLoggedIn ? (
        <>
          <button onClick={() => handleSecureNavigation('/app/citas')} style={pillStyles.blue}>
            <Calendar size={16} color="#60A5FA" />
            <span style={pillStyles.textBlue}>Mis Citas</span>
          </button>

          <button onClick={() => navigate('/hub')} style={pillStyles.yellow}>
            <Home size={16} color="#F59E0B" />
            <span style={pillStyles.textYellow}>Hub</span>
          </button>

          <button onClick={() => setIsMenuOpen(!isMenuOpen)} style={pillStyles.blue}>
            <Menu size={16} color="#60A5FA" />
            <span style={pillStyles.textBlue}>Menú</span>
          </button>

          <GlobalNotificationBell userId={profile?.perfil_usuario_id} />

          {isMenuOpen && (
            <div style={{
              position: 'absolute', top: '44px', right: '0', backgroundColor: '#1E293B',
              borderRadius: '16px', padding: '8px', display: 'flex', flexDirection: 'column',
              gap: '4px', zIndex: 100, boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
              minWidth: '180px'
            }}>
              {!isClinical && (
                <button onClick={() => { setIsMenuOpen(false); navigate('/app/become-pro'); }} style={pillStyles.dropdownItem}>
                   <span style={pillStyles.textBlue}>Soy Clínico</span>
                </button>
              )}
              {isClinical && (
                <button onClick={() => handleSecureNavigation('/app/admin/invite')} style={pillStyles.dropdownItem}>
                  <UserPlus size={16} color="#059669" />
                  <span style={pillStyles.textGreen}>Invitar Colega</span>
                </button>
              )}

              <button onClick={() => { setIsMenuOpen(false); navigate('/app/settings'); }} style={pillStyles.dropdownItem}>
                <User size={16} color="#CBD5E1" />
                <span style={pillStyles.textGray}>Mi Cuenta</span>
              </button>
              <button onClick={() => { setIsMenuOpen(false); AuthService.signOut(); }} style={{...pillStyles.dropdownItem, justifyContent: 'center', marginTop: '4px', backgroundColor: 'rgba(239, 68, 68, 0.1)'}}>
                <LogOut size={16} color="#DC2626" />
                <span style={{ color: '#DC2626', fontWeight: 700, fontSize: '13px' }}>Cerrar Sesión</span>
              </button>
            </div>
          )}
        </>
      ) : (
        <button onClick={() => navigate('/login')} style={pillStyles.blue}>
          <LogIn size={14} color="#0284C7" />
          <span style={pillStyles.textBlue}>Ingresar</span>
        </button>
      )}
    </div>
  );
};

const pillStyles = {
  blue: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '100px', backgroundColor: 'rgba(59, 130, 246, 0.15)', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 as const },
  textBlue: { color: '#60A5FA', fontWeight: 700, fontSize: '13px' },
  green: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '100px', backgroundColor: 'rgba(16, 185, 129, 0.15)', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 as const },
  textGreen: { color: '#34D399', fontWeight: 700, fontSize: '13px' },
  yellow: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '100px', backgroundColor: 'rgba(245, 158, 11, 0.15)', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 as const },
  textYellow: { color: '#F59E0B', fontWeight: 700, fontSize: '13px' },
  gray: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '100px', backgroundColor: 'rgba(255, 255, 255, 0.15)', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 as const },
  textGray: { color: '#CBD5E1', fontWeight: 700, fontSize: '13px' },
  red: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '100px', backgroundColor: 'rgba(239, 68, 68, 0.15)', border: 'none', cursor: 'pointer', flexShrink: 0 as const },
  dropdownItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderRadius: '12px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', width: '100%', justifyContent: 'flex-start', transition: 'background-color 0.2s' }
};

const ModalityButton = ({ icon: Icon, label, isActive, onPress }: any) => (
  <button 
    onClick={onPress} 
    style={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '10px 0',
      borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s',
      backgroundColor: isActive ? '#3B82F6' : 'transparent',
      boxShadow: isActive ? '0px 2px 8px rgba(0,0,0,0.1)' : 'none'
    }}
  >
    {Icon && <Icon size={14} color={isActive ? '#FFF' : '#64748B'} />}
    <span style={{ color: isActive ? '#FFFFFF' : '#94A3B8', fontSize: '12px', fontWeight: isActive ? 700 : 600 }}>{label}</span>
  </button>
);

const FilterPill = ({ icon: Icon, label, isActive, onPress, disabled = false }: any) => {
  return (
    <button 
      onClick={onPress} 
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '100px',
        border: '1px solid', borderColor: isActive ? '#3B82F6' : '#334155', cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: isActive ? '#3B82F6' : '#1E293B', opacity: disabled ? 0.5 : 1, transition: 'all 0.2s',
        whiteSpace: 'nowrap'
      }}
    >
      <Icon size={14} color={isActive ? '#FFF' : '#94A3B8'} />
      <span style={{ color: isActive ? '#FFFFFF' : '#64748B', fontSize: '13px', fontWeight: isActive ? 700 : 600 }}>{label}</span>
    </button>
  );
};

function EmptyState({ query, targetCity }: Readonly<{ query: string; targetCity: any }>) {
  if (query.trim() !== '') {
    return (
      <>
        <Search size={64} color="#CBD5E1" style={{ margin: '0 auto 16px auto', display: 'block' }} />
        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0F172A', marginBottom: '8px' }}>No hay especialistas</h2>
        <p style={{ fontSize: '15px', color: '#64748B', margin: 0 }}>Ajusta tu búsqueda, amplía la zona o intenta con otras especialidades para ver más opciones disponibles.</p>
      </>
    );
  }
  if (targetCity) {
    return (
      <>
        <MapPin size={64} color="#CBD5E1" style={{ margin: '0 auto 16px auto', display: 'block' }} />
        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0F172A', marginBottom: '8px' }}>Sin profesionales registrados</h2>
        <p style={{ fontSize: '15px', color: '#64748B', margin: 0 }}>Estamos expandiendo nuestra red a {targetCity.ciudad_nombre}. Vuelve pronto o intenta buscando en otra ubicación.</p>
      </>
    );
  }
  return (
    <>
      <Stethoscope size={64} color="#CBD5E1" style={{ margin: '0 auto 16px auto', display: 'block' }} />
      <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0F172A', marginBottom: '8px' }}>Directorio Vacío</h2>
      <p style={{ fontSize: '15px', color: '#64748B', margin: 0 }}>Estamos construyendo la red médica más exclusiva. Vuelve pronto.</p>
    </>
  );
}

function SearchResults({ loading, error, results, query, targetCity, onDoctorPress }: Readonly<{
  loading: boolean;
  error: string | null;
  results: SearchIndexItem[];
  query: string;
  targetCity: any;
  onDoctorPress: (item: SearchIndexItem) => void;
}>) {
  if (loading) {
    return (
      <div style={{ padding: '60px 32px', display: 'flex', justifyContent: 'center' }}>
        <DynamicLoader
          messages={["Optimizando la red médica local para tu ubicación...", "Conectando con especialistas de primer nivel...", "Buscando las mejores coincidencias cerca de ti..."]}
          color="#0F172A"
        />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '60px 32px', textAlign: 'center' }}>
        <AlertTriangle size={64} color="#EF4444" style={{ margin: '0 auto 16px auto', display: 'block' }} />
        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0F172A', marginBottom: '8px' }}>Algo salió mal</h2>
        <p style={{ fontSize: '15px', color: '#64748B', margin: 0 }}>{error}</p>
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div style={{ padding: '60px 32px', textAlign: 'center' }}>
        <EmptyState query={query} targetCity={targetCity} />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {results.map((item) => (
        <DoctorCard 
          key={item.id} 
          item={item} 
          onPress={() => onDoctorPress(item)} 
        />
      ))}
    </div>
  );
}

export function PatientSearchScreen() {
  const { 
    query, setQuery, results, loading, error, isClinical, isLoggedIn, checkingAuth, isProfileComplete,
    gpsDenied, requestGps, activeFilter, setActiveFilter, isCityPickerVisible, setIsCityPickerVisible,
    targetCity, setTargetCity, selectedModalities, toggleModality
  } = usePatientSearchData();

  const [showPaywall, setShowPaywall] = useState(false);
  const navigate = useNavigate();

  const handleDoctorPress = async (item: SearchIndexItem) => {
    if (!isLoggedIn) {
      setShowPaywall(true);
      return;
    }
    navigate(`/app/doctor/${item.id}`, { state: { initialData: item } });
  };

  return (
    <div style={{ flex: 1, backgroundColor: '#0F172A', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      
      {/* Header */}
      <div style={{ backgroundColor: '#0F172A', borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px', padding: '24px 20px', zIndex: 10, boxShadow: '0px 4px 20px rgba(15, 23, 42, 0.15)' }}>
        <ActionPills isClinical={isClinical} isLoggedIn={isLoggedIn} checkingAuth={checkingAuth} isProfileComplete={isProfileComplete} />
        
        <h1 style={{ fontSize: '28px', fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.5px', margin: '0 0 16px 0' }}>Aura Clínico</h1>

        {gpsDenied && (
          <button onClick={requestGps} style={{ backgroundColor: '#FFFBEB', borderRadius: '16px', padding: '16px', display: 'flex', alignItems: 'center', border: '1px solid #FEF3C7', marginBottom: '16px', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
            <MapPin size={20} color="#B45309" />
            <div style={{ flex: 1, marginLeft: '12px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#92400E' }}>Activa tu ubicación</div>
              <div style={{ fontSize: '13px', color: '#B45309', marginTop: '2px' }}>Para encontrar los médicos más cercanos a ti.</div>
            </div>
            <ChevronRight size={16} color="#B45309" />
          </button>
        )}

        <div style={{ marginBottom: '0' }}>
          <button 
            onClick={() => setIsCityPickerVisible(true)}
            style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.1)', padding: '6px 12px', borderRadius: '100px', marginBottom: '12px', gap: '4px', border: 'none', cursor: 'pointer' }}
          >
            <MapPin size={14} color="#94A3B8" />
            <span style={{ color: '#E2E8F0', fontSize: '13px', fontWeight: 600 }}>{targetCity ? targetCity.ciudad_nombre : 'Mi Ubicación'}</span>
            <ChevronDown size={12} color="#94A3B8" />
          </button>

          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '14px 16px', display: 'flex', alignItems: 'center', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)' }}>
            <Search size={20} color="#64748B" style={{ marginRight: '12px' }} />
            <input
              type="text"
              maxLength={100}
              placeholder="Busca por especialista, nombre o síntoma..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ flex: 1, fontSize: '16px', color: '#0F172A', fontWeight: 500, border: 'none', outline: 'none', padding: 0 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', backgroundColor: '#1E293B', borderRadius: '12px', padding: '4px', marginTop: '16px', marginBottom: '12px', border: '1px solid #334155' }}>
          <ModalityButton label="Todas" isActive={selectedModalities.length === 0} onPress={() => toggleModality('todas')} />
          <ModalityButton icon={Building} label="Presencial" isActive={selectedModalities.includes('presencial')} onPress={() => toggleModality('presencial')} />
          <ModalityButton icon={Video} label="Virtual" isActive={selectedModalities.includes('virtual')} onPress={() => toggleModality('virtual')} />
          <ModalityButton icon={Home} label="Domicilio" isActive={selectedModalities.includes('domicilio')} onPress={() => toggleModality('domicilio')} />
        </div>

        <div style={{ display: 'flex', overflowX: 'auto', gap: '8px', paddingBottom: '4px', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          <FilterPill icon={Star} label="Recomendados" isActive={activeFilter === 'recomendado'} onPress={() => setActiveFilter('recomendado')} />
          <FilterPill icon={Navigation} label="Cercanos" isActive={activeFilter === 'cercania'} onPress={() => setActiveFilter('cercania')} />
          <FilterPill icon={Award} label="Reputación" isActive={false} disabled={true} />
        </div>
      </div>

      {/* List Content */}
      <div style={{ flex: 1, backgroundColor: '#F8FAFC', paddingBottom: '40px' }}>
        <div style={{ padding: '24px 20px', flex: 1 }}>
          <PasskeyBanner />
          <SearchResults
            loading={loading}
            error={error}
            results={results}
            query={query}
            targetCity={targetCity}
            onDoctorPress={handleDoctorPress}
          />
        </div>
      </div>

      <CityPickerModal
        visible={isCityPickerVisible}
        onClose={() => setIsCityPickerVisible(false)}
        onSelectCity={(city) => {
          setTargetCity(city);
          if (city !== null && activeFilter === 'cercania') {
            setActiveFilter('recomendado');
          }
        }}
      />

      {showPaywall && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '24px', padding: '32px', width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}>
            <div style={{ width: '64px', height: '64px', backgroundColor: '#F0F9FF', borderRadius: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px' }}>
              <Lock size={32} color="#0284C7" />
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#0F172A', marginBottom: '12px', textAlign: 'center', margin: '0 0 12px 0' }}>Ingresa a Aura Clínico</h2>
            <p style={{ fontSize: '15px', color: '#475569', textAlign: 'center', lineHeight: '22px', marginBottom: '32px', margin: '0 0 32px 0' }}>Para ver el perfil completo, opiniones y la agenda de este especialista debes iniciar sesión.</p>
            
            <button 
              onClick={async () => {
                setShowPaywall(false);
                try {
                  await AuthService.signInWithGoogle();
                } catch (e) {
                  console.error("Error al iniciar sesión con Google desde el paywall", e);
                }
              }}
              style={{ backgroundColor: '#0284C7', color: '#FFFFFF', padding: '16px 24px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', border: 'none', cursor: 'pointer', fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '10px' }}>
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#FFFFFF"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#FFFFFF"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FFFFFF"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#FFFFFF"/>
              </svg>
              Ingresar con Google
            </button>
            
            <button onClick={() => setShowPaywall(false)} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: '15px', fontWeight: 600, cursor: 'pointer', padding: '12px 24px', width: '100%' }}>
              Prefiero seguir a ciegas
            </button>
          </div>
        </div>
      )}

      <style>{`
        ::-webkit-scrollbar { display: none; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
